import Link from "next/link";
import { Fragment, type ReactNode } from "react";

interface LinkedTextProps {
  // Copy with optional `[label](/route)` links and `**bold**` markers.
  text: string;
  linkClassName?: string;
}

// Splits on links first, then on bold, so copy stays a plain string in the
// data files and can't inject markup.
const linkPattern = /\[([^\]]+)\]\(([^)\s]+)\)/g;
const boldPattern = /\*\*(.+?)\*\*/g;

const renderBold = (text: string, keyPrefix: string) =>
  text.split(boldPattern).map((part, index) =>
    index % 2 === 1 ? (
      <strong key={`${keyPrefix}-${index}`} className="font-semibold text-ink">
        {part}
      </strong>
    ) : (
      <Fragment key={`${keyPrefix}-${index}`}>{part}</Fragment>
    ),
  );

// Page copy with inline links (the preview's `.inline-link`). Internal
// routes go through next/link; absolute URLs open in a new tab.
const LinkedText = ({
  text,
  linkClassName = "font-semibold text-primary underline decoration-[1.5px] underline-offset-[3px] hover:text-ink",
}: LinkedTextProps) => {
  // split() with two capture groups yields [text, label, href, text, ...].
  const parts = text.split(linkPattern);
  const nodes: ReactNode[] = [];
  for (let index = 0; index < parts.length; index += 3) {
    nodes.push(renderBold(parts[index], `t${index}`));
    const label = parts[index + 1];
    const href = parts[index + 2];
    if (label === undefined || href === undefined) continue;
    nodes.push(
      /^https?:\/\//.test(href) ? (
        <a
          key={`l${index}`}
          href={href}
          target="_blank"
          rel="noopener"
          className={linkClassName}
        >
          {label}
        </a>
      ) : (
        <Link key={`l${index}`} href={href} className={linkClassName}>
          {label}
        </Link>
      ),
    );
  }
  return <>{nodes}</>;
};

export default LinkedText;
